import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { SpaceRepository } from '../state/space.repository';
import { URLService } from './dynamic-url.service';
import { TelemetryService } from './telemetry.service';
import { UserService } from './user.service';
import { FLAGS, FlagsService } from './flags.service';

export enum HB_VM_STATE {
  Unknown,
  Error,
  Terminated,
  NotFound,
  Alive,
}

export interface HbSessionResponse {
  state: HB_VM_STATE;
  data?: {
    session_id: string;
    embed_url: string;
    admin_token: string;
    termination_date?: string;
    snapshot_session_id?: string;
    is_portal_session?: boolean;
    // Per-session Portal MCP token (Portal PR #294). Persisted onto the
    // web-viewer board item's browser_state. Portal sessions only.
    remote_control_token?: string;
  };
}

export interface WebViewerConfig {
  webgl: boolean;
  adblock: boolean;
  defaultUrl?: string;
  country?: string;
  isPortalSession?: boolean;
}

/*
Provide access to Hyperbeam (co-browser) functionality.
*/
@Injectable({
  providedIn: 'root',
})
export class HyperbeamService {
  constructor(
    private urlService: URLService,
    private http: HttpClient,
    private telemetry: TelemetryService,
    private spaceRepo: SpaceRepository,
    private userService: UserService,
    private flagService: FlagsService,
  ) {}

  createHbSession$(
    existingSessionId: string | undefined,
    webViewerConfig: WebViewerConfig,
    checkForSessionTermination: boolean = false,
  ): Observable<HbSessionResponse> {
    const isPortal = existingSessionId
      ? webViewerConfig.isPortalSession // For existing sessions, check session config
      : this.flagService.isFlagEnabled(FLAGS.ENABLE_PORTAL); // For new sessions, check feature flag

    const body = {
      userId: this.userService.userId,
      spaceId: this.spaceRepo.activeSpaceId,
      checkForSessionTermination,
      webgl: isPortal ? true : webViewerConfig.webgl,
      adblock: webViewerConfig.adblock,
      defaultUrl: webViewerConfig.defaultUrl,
      country: webViewerConfig.country,
    };
    const baseUrl = this.urlService.getDynamicUrl();
    const pathType = isPortal ? 'portal' : 'hb';
    const url = `${baseUrl}/${pathType}/vm${existingSessionId ? `/${existingSessionId}` : ''}`;
    return this.http.post<HbSessionResponse>(url, body);
  }

  async createHbSession(
    existingSessionId: string | undefined,
    webViewerConfig: WebViewerConfig,
    checkForSessionTermination = false,
  ) {
    const hbSessionResponse = await firstValueFrom(
      this.createHbSession$(existingSessionId, webViewerConfig, checkForSessionTermination),
    );
    if (
      hbSessionResponse &&
      hbSessionResponse.state === HB_VM_STATE.Alive &&
      hbSessionResponse.data
    ) {
      this.telemetry.event('Web Viewer VM Created', {
        sessionId: hbSessionResponse.data.session_id,
        spaceId: this.spaceRepo.activeSpaceId,
      });

      if (hbSessionResponse.data.is_portal_session === true) {
        this.telemetry.event('Portal WebViewer Session Created');
      }
    }
    return hbSessionResponse;
  }
}
